// DNCM Vision — i18n + currency context
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_LANG = "dncm.lang";
const STORAGE_CCY = "dncm.ccy";

// All UI strings used across the site. Long agent descriptions stay FR.
const STRINGS = {
  fr: {
    nav_vision: "Vision",
    nav_escouade: "L'Escouade",
    nav_vault: "The Vault",
    nav_studio: "Studio",
    nav_cockpit: "Cockpit",
    nav_test_mirror: "Test Real Agent Mirror",
    cta_voir_agents: "Voir l'Escouade",
    cta_cockpit: "Accéder au cockpit",
    cta_whatsapp: "Parler sur WhatsApp",
    hero_eyebrow: "DNCM Solution · Cockpit immersif",
    hero_subtitle: "Plateforme intelligente d'agents IA.",
    hero_tagline: "« Vous ne louez plus votre technologie. Vous la possédez. »",
    activate: "Activer",
    activation_label: "Activation",
    open: "Ouvrir",
    order_btn: "Commander l'Agent",
    confirm_order: "Confirmer la commande",
    name: "Nom",
    email: "Email",
    message: "Message (optionnel)",
    success_lead: "Demande reçue. Nous vous contactons rapidement.",
    success_order: "Commande reçue. Nous vous contactons très vite.",
    success_amb: "Demande Ambassadeur reçue. À très vite !",
    success_mirror: "Simulation prête.",
    twin_title: "Digital Twin",
    twin_subtitle: "État du système · temps réel",
    twin_servers: "Serveurs en ligne",
    twin_uptime: "Disponibilité",
    twin_tokens_used: "Tokens consommés",
    twin_tokens_left: "Tokens restants",
    twin_latency: "Latence moyenne",
    twin_threads: "Conversations actives",
    studio_title: "Configurez votre escouade",
    studio_subtitle: "Selena Matrix — sélectionnez votre secteur, recevez un pack sur-mesure.",
    sector_label: "Votre secteur",
    growth_title: "Simulateur de Croissance",
    growth_subtitle: "Évolution prévisionnelle de vos revenus sur 12 mois.",
    months: ["M01","M02","M03","M04","M05","M06","M07","M08","M09","M10","M11","M12"],
    revenue: "Revenus",
    leads: "Leads",
    closed: "Conclus",
    token_bank_title: "Token Bank",
    token_bank_subtitle: "Achetez vos crédits d'agents en un clic.",
    pack_starter: "Starter",
    pack_growth: "Croissance",
    pack_empire: "Empire",
    buy_pack: "Acheter ce pack",
    pay_now: "Payer maintenant",
    activate_system: "Activer mon système",
    payment_title: "Paiement simple et rapide",
    payment_subtitle: "Choisissez votre moyen de paiement préféré.",
    vault_title: "The Vault — Souveraineté Data",
    vault_subtitle: "Vos données restent vôtres. Trois niveaux de protection, zéro dépendance GAFAM.",
    sovereignty_levels: ["Agile", "Structuré", "Souverain"],
    gafam_title: "Indépendance GAFAM",
    gafam_text:
      "DNCM Vision n'envoie aucune de vos données stratégiques aux grands acteurs américains. Vous gardez la propriété et le contrôle complet — chez vous, sur votre infrastructure.",
    ambassador_banner: "Activer l'Offre Ambassadeur (1000 PHP)",
    ambassador_eyebrow: "Offre fondatrice — places limitées",
    cascade_title: "Cascade de Revenus",
    cascade_caption: "Projection mensuelle (estimation conservative).",
    chat_input_placeholder: "Parlez à",
    capacities_title: "Capacités",
    no_simulation: "Le résultat s'affichera ici après la simulation.",
    simulating: "Analyse en cours…",
    test_mirror_eyebrow: "Testez nos agents sur vos données",
    test_mirror_title: "Agent Miroir — simulez votre IA en 30 secondes.",
    test_mirror_caption:
      "Entrez l'URL de votre entreprise. Nous générons un aperçu de ce que votre système DNCM Vision pourrait produire dès demain.",
    simulate_btn: "Simuler mon IA",
    create_my_system: "Créer mon système IA",
  },
  en: {
    nav_vision: "Vision",
    nav_escouade: "The Squad",
    nav_vault: "The Vault",
    nav_studio: "Studio",
    nav_cockpit: "Cockpit",
    nav_test_mirror: "Test Real Agent Mirror",
    cta_voir_agents: "See the Squad",
    cta_cockpit: "Open the Cockpit",
    cta_whatsapp: "Talk on WhatsApp",
    hero_eyebrow: "DNCM Solution · Immersive cockpit",
    hero_subtitle: "An intelligent AI agent platform.",
    hero_tagline: "“You no longer rent your technology. You own it.”",
    activate: "Activate",
    activation_label: "Activation",
    open: "Open",
    order_btn: "Order this Agent",
    confirm_order: "Confirm order",
    name: "Name",
    email: "Email",
    message: "Message (optional)",
    success_lead: "Got it. We will contact you very soon.",
    success_order: "Order received. We will reach out very soon.",
    success_amb: "Ambassador request received. Talk soon!",
    success_mirror: "Simulation ready.",
    twin_title: "Digital Twin",
    twin_subtitle: "System state · real time",
    twin_servers: "Servers online",
    twin_uptime: "Uptime",
    twin_tokens_used: "Tokens consumed",
    twin_tokens_left: "Tokens remaining",
    twin_latency: "Avg latency",
    twin_threads: "Active threads",
    studio_title: "Configure your squad",
    studio_subtitle: "Selena Matrix — pick your sector, get a tailored pack.",
    sector_label: "Your sector",
    growth_title: "Growth Simulator",
    growth_subtitle: "Projected revenue evolution over 12 months.",
    months: ["M01","M02","M03","M04","M05","M06","M07","M08","M09","M10","M11","M12"],
    revenue: "Revenue",
    leads: "Leads",
    closed: "Closed",
    token_bank_title: "Token Bank",
    token_bank_subtitle: "Buy your agent credits in one click.",
    pack_starter: "Starter",
    pack_growth: "Growth",
    pack_empire: "Empire",
    buy_pack: "Buy this pack",
    pay_now: "Pay now",
    activate_system: "Activate my system",
    payment_title: "Fast and simple payment",
    payment_subtitle: "Choose your preferred method.",
    vault_title: "The Vault — Data sovereignty",
    vault_subtitle: "Your data stays yours. Three levels of protection, zero GAFAM dependence.",
    sovereignty_levels: ["Agile", "Structured", "Sovereign"],
    gafam_title: "GAFAM independence",
    gafam_text:
      "DNCM Vision never ships your strategic data to the US tech giants. You keep ownership and full control — at home, on your infrastructure.",
    ambassador_banner: "Activate the Ambassador Offer (1000 PHP)",
    ambassador_eyebrow: "Founder offer — limited seats",
    cascade_title: "Revenue Cascade",
    cascade_caption: "Monthly projection (conservative estimate).",
    chat_input_placeholder: "Chat with",
    capacities_title: "Capabilities",
    no_simulation: "Results will appear here after simulation.",
    simulating: "Analyzing…",
    test_mirror_eyebrow: "Test our agents with your data",
    test_mirror_title: "Agent Mirror — simulate your AI in 30 seconds.",
    test_mirror_caption:
      "Enter your company URL. We generate a preview of what your DNCM Vision system could deliver from day one.",
    simulate_btn: "Simulate my AI",
    create_my_system: "Build my AI system",
  },
};

// Currency definitions (rates relative to 1 PHP).
// Source: indicative — easy to update.
export const CURRENCIES = {
  PHP: { code: "PHP", symbol: "₱", rate: 1, locale: "en-PH" },
  EUR: { code: "EUR", symbol: "€", rate: 0.016, locale: "fr-FR" },
  USD: { code: "USD", symbol: "$", rate: 0.018, locale: "en-US" },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_LANG) || "fr");
  const [ccy, setCcy] = useState(() => localStorage.getItem(STORAGE_CCY) || "PHP");

  useEffect(() => {
    localStorage.setItem(STORAGE_LANG, lang);
  }, [lang]);
  useEffect(() => {
    localStorage.setItem(STORAGE_CCY, ccy);
  }, [ccy]);

  const value = useMemo(() => {
    const dict = STRINGS[lang] || STRINGS.fr;
    const t = (key) => (key in dict ? dict[key] : key);
    const currency = CURRENCIES[ccy] || CURRENCIES.PHP;

    const convert = (amountPhp) => amountPhp * currency.rate;

    const formatPrice = (amountPhp, opts = {}) => {
      const { fractionDigits = currency.code === "PHP" ? 0 : 2, withSymbol = true } = opts;
      const value = amountPhp * currency.rate;
      const num = new Intl.NumberFormat(currency.locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(value);
      if (!withSymbol) return num;
      return `${num} ${currency.code}`;
    };

    return {
      lang,
      setLang,
      ccy,
      setCcy,
      t,
      currency,
      convert,
      formatPrice,
    };
  }, [lang, ccy]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
